import { telemetry } from '../../../../services/backend';
import { MarkerItem } from '../../../../services/store/modules/marketplace/models';

const markersCache: {
  [key: string | number]: MarkerItem & {
    markerImage?: string;
    markerSize1?: number;
    markerSize2?: number;
    markerSize3?: number;
  };
} = {};

export const updateMarkersCacheWithGivenItems = async (
  items: MarkerItem[],
): Promise<boolean> => {
  if (items && items.length) {
    telemetry(
      telemetry.DISABLED,
      `[markersCache] attempting to add ${items.length} to ${
        Object.keys(markersCache).length
      } existing items`,
    );
    items
      // Decorate the data with marker sizes and marketplace data
      .map((i) => {
        const isPartner = !!i.partner;
        const markerImage =
          i.markerImage || (isPartner && i.image) || i.markerType;
        return {
          ...i,
          markerImage,
          markerSize1: 0,
          markerSize2: isPartner ? 0.4 : 0.25,
          markerSize3: isPartner ? 0.5 : 0.4,
        };
      })
      .forEach((item) => {
        const hash = `${item.markerType}::${item.id}`;
        if (!markersCache[hash]) {
          markersCache[hash] = item;
          return true;
        }
        // Item already exists, ignore it.
        return false;
      });
    telemetry(
      telemetry.DISABLED,
      `[markersCache] now contains ${Object.keys(markersCache).length} items`,
    );
  }
  return false;
};

interface UpdateShownMarkersConfig {
  bounds: {
    lat: number;
    lon: number;
  }[];
  selectedMarkerIds: unknown[];
  selectedLayerTypes: unknown[];
}

export const updateShownMarkers = async ({
  bounds,
  selectedMarkerIds,
  selectedLayerTypes,
}: UpdateShownMarkersConfig): Promise<MarkerItem[]> => {
  const selectedMarkersSet = new Set(selectedMarkerIds);
  const selectedLayerSet = new Set(selectedLayerTypes);

  telemetry(
    telemetry.DISABLED,
    `[markersCache] updating for ${selectedLayerSet.size} selectedLayers and ${selectedMarkersSet.size} selectedMarkerIds`,
  );
  telemetry(
    telemetry.DISABLED,
    `[markersCache] current bounds ${JSON.stringify(bounds)}`,
  );

  const selected = Object.values(markersCache).filter(
    (marker) =>
      // Only allow markers that are a type in selectedLayerTypes or this are a selectedMarkerId
      selectedLayerSet.has(marker.markerType) ||
      selectedMarkersSet.has(marker.id),
  );

  telemetry(
    telemetry.DISABLED,
    `[markersCache] found ${selected.length} items based on selection`,
  );

  if (!bounds[0] || !bounds[1]) {
    return [];
  }

  const inArea = selected
    // Filter by bounding box
    .filter(
      (marker) =>
        /* eslint-disable no-underscore-dangle */
        // WATCH OUT HERE. Bounds are [[MAX_LAT, MIN_LON], [MIN_LAT, MAX_LON]]
        marker.latLon.lat < bounds[0].lat + 0.03 &&
        marker.latLon.lat > bounds[1].lat - 0.03 &&
        marker.latLon.lon > bounds[0].lon - 0.03 &&
        marker.latLon.lon < bounds[1].lon + 0.03,
      /* eslint-enable no-underscore-dangle */
    );

  telemetry(
    telemetry.DISABLED,
    `[markersCache] looking at bound ${JSON.stringify(bounds)}`,
  );
  telemetry(
    telemetry.DISABLED,
    `[markersCache] found ${inArea.length} items based on location`,
  );

  return inArea;
};

export const clearMarkersCache = (): void => {
  Object.keys(markersCache).forEach((marker) => {
    delete markersCache[marker];
  });
};
